import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Typography } from '@mui/material';
import { Hub, useAuth } from '@teamexos/fit-shared';
import Container from 'components/Container';
import AppOverlay from 'components/common/AppOverlay';
import useWindowSize from 'hooks/useWindowSize';
import Authenticated from 'layouts/Authenticated';
import Guest from 'layouts/Guest';
import { useIdleTimerContext } from 'providers/IdleTimerContext';
import React, { useEffect } from 'react';
import { DOWNLOAD_APP_PATH, PHONE_USER_AGENT_REGEX } from 'utils/appStore';
import logger from 'utils/logger';

const App = () => {
  const { width } = useWindowSize();
  const { pathname } = window.location;
  const { isLoading, isLoggedIn } = useAuth();

  const { start } = useIdleTimerContext();

  useEffect(
    () =>
      Hub.listen('auth', ({ payload }) => {
        logger.debug('Hub event', {
          event: payload.event,
          message: payload.message,
        });

        const message = payload.message ? `:${payload.message}` : '';

        switch (payload.event) {
          case 'customOAuthState':
            // Don't log this as it may have the email in it
            break;
          case 'signInWithRedirect':
          case 'signedOut':
          case 'tokenRefresh':
            datadogRum.addAction('cognito', {
              category: 'cognito',
              message: `${payload.event}${message}`,
            });
            break;
          case 'signedIn':
            logger.debug('SignedIn User', payload.data);
            datadogRum.addAction('cognito', {
              category: 'cognito',
              message: `${payload.event}${message}`,
              data: payload.data,
            });
            break;
          case 'signInWithRedirect_failure':
          case 'tokenRefresh_failure':
            logger.debug(payload.data);
            datadogRum.addAction('cognito', {
              category: 'cognito',
              message: `${payload.event}${message}`,
              data: payload.data,
              level: 'error',
            });
            if (
              payload.data &&
              typeof payload.data === 'object' &&
              'error' in payload.data &&
              payload.data.error
            ) {
              datadogRum.addError(payload.data.error as Error);
            }
            break;
          default:
            datadogLogs.logger.warn(
              `Unhandled cognito event: ${(payload as { event?: string }).event}`,
            );
            logger.debug('Unhandled event');
        }
      }),
    [],
  );

  useEffect(() => {
    if (isLoggedIn) {
      start();
    }
  }, [isLoggedIn, start]);

  const isPhone =
    width < 744 && PHONE_USER_AGENT_REGEX.test(navigator.userAgent);
  if (
    isPhone &&
    !pathname.startsWith('/pepsico') &&
    !pathname.startsWith('/callback') &&
    !pathname.startsWith(DOWNLOAD_APP_PATH)
  ) {
    return <AppOverlay />;
  }

  if (isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return isLoggedIn ? <Authenticated /> : <Guest />;
};

export default App;
